import React, { useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import ButtonBase from '@material-ui/core/ButtonBase';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useIntl } from 'react-intl';
import { Link, withRouter } from 'react-router-dom';

import AdminIcon from '../icons/AdminIcon.jsx';
import CardholderIcon from '../icons/CardholderIcon.jsx';
import DashboardIcon from '../icons/DashboardIcon.jsx';
import HelpIcon from '../icons/HelpIcon.jsx';
import HomeIcon from '../icons/HomeIcon.jsx';
import ReportIcon from '../icons/ReportIcon.jsx';
import SettingsIcon from '../icons/SettingsIcon.jsx';
import logo from '../icons/premisys_logo.svg';
import logoDark from '../icons/premisys_logo_dark.svg';
import getNameFromToken from '../util/jwtToken';

const drawerWidth = 260;

const useStyles = makeStyles((theme) => ({
  drawer: {
    [theme.breakpoints.up('sm')]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  logo: {
    maxWidth: drawerWidth,
    maxHeight: 64, // TODO: set based on height of app bar
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: '0.3rem',
  },
  bottomPush: {
    position: 'absolute',
    width: '100%',
    bottom: 0,
    paddingBottom: '2.5rem',
    paddingLeft: theme.spacing(1.8),
  },
  // necessary for content to be below app bar
  toolbar: theme.mixins.toolbar,
  drawerPaper: {
    width: drawerWidth,
    border: 0,
  },
  textLabel: {
    color: 'grey',
  },
  iconColor: {
    color: 'secondary',
  },
  selected: {
    borderLeft: '5px solid #5ba9e1',
    paddingLeft: '25px',
  },
  menuitem: {
    paddingLeft: '30px',
  },
  userMenuItem: {
    paddingLeft: '23px',
    paddingTop: '6px',
    paddingBottom: '6px',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
  userName: {
    paddingLeft: '23px',
  },
  avatarMenuItem: {
    width: drawerWidth,
  },
}));

const Sidebar = ({
  type,
  mobileOpen,
  handleDrawerToggle,
  setShowHelpDialog,
  isMobile,
  apiToken,
  setApiToken,
  setColorTheme,
  window,
  ...props
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAvatarMenu = () => {
    setAnchorEl(null);
  };

  const iconclick = () => {
    props.history.push('/cardholders');
  };

  const activeRoute = (routeName) => {
    return props.location.pathname === routeName;
  };

  const menuFactory = (id, name, route, enabled) => {
    return {
      text: intl.formatMessage({
        id: `sidebar.${name.toLowerCase()}`,
        description: `sidebar link: ${name.toLowerCase()}`,
        defaultMessage: name,
      }),
      icon: name.toUpperCase(),
      disabled: !enabled ? true : undefined,
      path: enabled ? route : undefined,
      selected: enabled ? activeRoute(route) : false,
      id,
    };
  };

  const initList = [
    menuFactory(0, 'Home', null, false),
    menuFactory(1, 'Dashboard', '/dashboard', false),
    menuFactory(2, 'Cardholders', '/cardholders', true),
    menuFactory(3, 'Administration', '/admin', true),
    menuFactory(4, 'Reports', null, false),
  ];

  const [upperList, setUpperList] = useState(initList);

  const handleClose = () => {
    if (mobileOpen) {
      handleDrawerToggle();
      handleCloseAvatarMenu();
    }
  };

  const handleTheme = () => {
    handleClose();
    const newTheme = theme.palette.type === 'dark' ? 'light' : 'dark';
    setColorTheme(newTheme);
  };

  const handleLogout = () => {
    handleClose();
    setApiToken(null);
  };

  const getMenuItemIcon = (icon, isSelected) => {
    switch (icon) {
      case 'HOME':
        return <HomeIcon color={isSelected ? 'secondary' : 'inherit'} />;
      case 'DASHBOARD':
        return <DashboardIcon color={isSelected ? 'secondary' : 'inherit'} />;
      case 'CARDHOLDERS':
        return <CardholderIcon color={isSelected ? 'secondary' : 'inherit'} />;
      case 'ADMINISTRATION':
        return <AdminIcon color={isSelected ? 'secondary' : 'inherit'} />;
      case 'REPORTS':
        return <ReportIcon color={isSelected ? 'secondary' : 'inherit'} />;
      case 'HELP':
        return <HelpIcon color={isSelected ? 'secondary' : 'inherit'} />;
      case 'SETTINGS':
        return <SettingsIcon color={isSelected ? 'secondary' : 'inherit'} />;
      default:
        return null;
    }
  };

  const handleSelected = (_, key) => {
    for (let i = 0; i < upperList.length; i += 1) {
      upperList.splice(i, 1, {
        text: intl.formatMessage({
          id: `sidebar.${upperList[i].text.toLowerCase()}`,
          description: `sidebar link: ${upperList[i].text.toLowerCase()}`,
          defaultMessage: upperList[i].text,
        }),
        icon: upperList[i].icon,
        path: upperList[i].path,
        selected: upperList[i].id === key.id,
        id: upperList[i].id,
        disabled: upperList[i].disabled,
      });
    }
    setUpperList(upperList);
  };

  const container = window !== undefined ? () => window().document.body : undefined;

  const drawer = (
    <div>
      <div className={classes.toolbar}>
        <img
          className={classes.logo}
          src={theme.palette.type === 'dark' ? logoDark : logo}
          alt="PremiSys"
          onClick={() => iconclick()}
          style={{ cursor: 'pointer' }}
        />
      </div>
      <Divider />

      {isMobile && (
        <>
          <div className={classes.userMenuItem} onClick={handleClick}>
            <ButtonBase
              className={classes.profile}
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={() => {}}
            >
              <Avatar />
              <Typography className={classes.userName}>{getNameFromToken(apiToken)}</Typography>
              <ExpandMoreIcon
                style={{ fill: theme.palette.neutral.main, marginRight: theme.spacing(0.5) }}
              />
            </ButtonBase>
          </div>
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleCloseAvatarMenu}
          >
            {/* TODO: The Following code snippet is required for the next release */}
            {/* <MenuItem onClick={handleClose}>Account Settings</MenuItem> */}
            <MenuItem onClick={handleTheme} className={classes.avatarMenuItem}>
              {theme.palette.type === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </MenuItem>
            {/* <MenuItem onClick={handleClose}>Feedback</MenuItem> */}
            <MenuItem onClick={handleLogout} className={classes.avatarMenuItem}>
              Sign Out
            </MenuItem>
          </Menu>
        </>
      )}
      <MenuList>
        {upperList.map((item) => (
          <MenuItem
            className={item.selected ? classes.selected : classes.menuitem}
            component={Link}
            to={item.path}
            onClick={(e) => {
              handleClose();
              if (!item.disabled) {
                handleSelected(e, item);
              }
            }}
            selected={activeRoute(item.path)}
            key={item.id}
          >
            <ListItemIcon key={item.id}>{getMenuItemIcon(item.icon, item.selected)}</ListItemIcon>
            <ListItemText
              primary={item.text}
              className={item.disabled ? classes.textLabel : null}
              onClick={() => {}}
            />
          </MenuItem>
        ))}
      </MenuList>
      <MenuList className={classes.bottomPush}>
        <MenuItem onClick={props.handleToggleDrawer('right', true)} key="Help Center">
          <ListItemIcon>{getMenuItemIcon('HELP')}</ListItemIcon>
          <ListItemText
            primary={intl.formatMessage({
              id: 'sidebar.help',
              description: 'help center',
              defaultMessage: 'Help Center',
            })}
          />
        </MenuItem>
        {/* <MenuItem
          component={Link}
          to="/settings"
          onClick={handleClose}
          selected={activeRoute('/settings')}
          key="Settings"
        >
          <ListItemIcon>{getMenuItemIcon('SETTINGS')}</ListItemIcon>
          <ListItemText
            primary={intl.formatMessage({
              id: 'sidebar.settings',
              description: 'settings',
              defaultMessage: 'Settings',
            })}
          />
        </MenuItem> */}
      </MenuList>
    </div>
  );

  return (
    <nav className={classes.drawer} aria-label="navigation menu">
      {isMobile ? (
        <Drawer
          container={container}
          variant="temporary"
          anchor={theme.direction === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{ paper: classes.drawerPaper }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer classes={{ paper: classes.drawerPaper }} variant="permanent" open>
          {drawer}
        </Drawer>
      )}
    </nav>
  );
};

export default withRouter(Sidebar);
